import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { getBdcInvoice } from "../../../services";
import {
  cancelBdcCreditNote,
  createBdcCreditNote,
  deleteBdcCreditNote,
  getBdcCreditableItems,
  getBdcCreditNote,
  getBdcCreditNotePdf,
  issueBdcCreditNote,
  updateBdcCreditNote,
} from "../../../services/bdcCreditNote";
import { featureAccess } from "../../../permissions/access";
import { BdcCreditableInvoice, BdcCreditNoteDetail } from "../../../types/bdc.type";
import { useUserRole } from "../../../utils/hooks/useUserRole";
import { BdcApiError, readBdcError } from "../apiError";
import BranchChip from "../Branch/BranchChip";
import { useBdcBranches } from "../Branch/useBdcBranches";
import {
  BDC_CREDIT_NOTE_DEFAULT_REASON,
  BDC_CREDIT_NOTES_PATH,
  BDC_CREDIT_NOTE_REF_NO_MAX,
  BDC_RETURN_DOC_NO_LENGTH,
  bdcCreditNoteLink,
  bdcSettingsLink,
  bdcShipmentLink,
} from "../constants";
import { baht, pcs, rowsOf, thaiDate, thaiDateTime, todayBkk } from "../format";
import { openPdfResponse } from "../Invoice/pdf";
import ReasonDialog from "../ReasonDialog";
import { CARD_SX, TABLE_SX } from "../styles";
import { useBdcDcs } from "../useBdcDcs";
import {
  canCancelNote,
  cnLineTotal,
  cnLineVat,
  CnDraftHeader,
  CnDraftLine,
  cnTotals,
  draftProblem,
  linesFromDetail,
  overCreditedLines,
  parseQty,
  saveBody,
  withAvailability,
} from "./cn";
import CreditNoteStatusChip from "./CreditNoteStatusChip";
import InvoicePicker from "./InvoicePicker";

const NEW = "new";

const emptyHeader = (): CnDraftHeader => ({
  dc_code: "",
  return_doc_no: "",
  note_date: todayBkk(),
  reason: BDC_CREDIT_NOTE_DEFAULT_REASON,
});

function TotalsBox({ subtotal, vat, total }: Readonly<{ subtotal: number; vat: number; total: number }>) {
  return (
    <Box sx={{ minWidth: 260, ml: "auto" }} data-testid="bdc-cn-totals">
      {[
        ["มูลค่าสินค้าที่ลด", subtotal],
        ["ภาษีมูลค่าเพิ่ม 7%", vat],
      ].map(([label, value]) => (
        <Stack key={label as string} direction="row" justifyContent="space-between" py={0.25}>
          <Typography variant="body2">{label}</Typography>
          <Typography variant="body2">{baht(value as number)}</Typography>
        </Stack>
      ))}
      <Stack direction="row" justifyContent="space-between" py={0.5} sx={{ borderTop: "1px solid #E3E5EA", mt: 0.5 }}>
        <Typography fontWeight={700}>รวมลดหนี้</Typography>
        <Typography fontWeight={700} color="primary">
          {baht(total)}
        </Typography>
      </Stack>
    </Box>
  );
}

/**
 * BDC · CN หนึ่งใบ — `new` or a draft is the form: head (DC, เลขเอกสารคืน, วันที่, เหตุผล) and lines
 * picked from the DC's issued INVs (pieces up to what each INV × product has left — over is a
 * warning on the draft, a refusal at ออก CN; ref defaults to the PO, editable for a WB return
 * number). Issued / sent / cancelled is read-only with PDF and ยกเลิก.
 */
export default function BdcCreditNote() {
  const role = useUserRole();
  const canWrite = featureAccess.bdcCreditNoteWrite(role);
  const navigate = useNavigate();
  const { uuid = NEW } = useParams<{ uuid: string }>();
  const [params] = useSearchParams();
  const fromInvoiceNo = uuid === NEW ? params.get("invoice_no") : null;
  const isNew = uuid === NEW;

  const { branches } = useBdcBranches();
  const { dcs } = useBdcDcs();

  const [detail, setDetail] = useState<BdcCreditNoteDetail | null>(null);
  const [header, setHeader] = useState<CnDraftHeader>(emptyHeader);
  const [lines, setLines] = useState<CnDraftLine[]>([]);
  const [savedBody, setSavedBody] = useState<string | null>(null);
  const [lineIsTest, setLineIsTest] = useState<boolean | null>(null);
  const [initialInvoice, setInitialInvoice] = useState<
    Pick<BdcCreditableInvoice, "invoice_no" | "po_number" | "is_test" | "invoice_date"> | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<"save" | "issue" | "delete" | "pdf" | null>(null);
  const [failure, setFailure] = useState<BdcApiError | null>(null);
  const [confirm, setConfirm] = useState<"issue" | "delete" | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);

  const editable = canWrite && (isNew || detail?.status === "draft");

  /** Look up each INV of the lines so they show (and check) what is left to credit. */
  const fillAvailability = useCallback(async (base: CnDraftLine[], saved: Record<string, number>) => {
    const invoiceNos = Array.from(new Set(base.map((l) => l.invoice_no)));
    let next = base;
    for (const invoiceNo of invoiceNos) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await getBdcCreditableItems(invoiceNo);
        next = withAvailability(next, invoiceNo, rowsOf(data.data), saved);
      } catch {
        // leave this INV's lines as "unknown"; the server still checks at ออก CN
      }
    }
    setLines((current) =>
      // keep what the user typed meanwhile; only the availability comes from the lookup
      current.map((line) => {
        const found = next.find((l) => l.key === line.key);
        return found ? { ...line, available: found.available, invoiced_qty: found.invoiced_qty } : line;
      }),
    );
  }, []);

  const applyDetail = useCallback(
    (data: BdcCreditNoteDetail) => {
      setDetail(data);
      const nextHeader = {
        dc_code: data.dc_code,
        return_doc_no: data.return_doc_no,
        note_date: data.note_date,
        reason: data.reason,
      };
      const nextLines = linesFromDetail(data);
      const saved = Object.fromEntries(nextLines.map((l) => [l.key, parseQty(l.qty) ?? 0]));
      setHeader(nextHeader);
      setLines(nextLines);
      setLineIsTest(data.items.length > 0 ? data.is_test : null);
      setSavedBody(JSON.stringify(saveBody(nextHeader, nextLines)));
      if (data.status === "draft") fillAvailability(nextLines, saved);
    },
    [fillAvailability],
  );

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const {
        data: { data },
      } = await getBdcCreditNote(uuid);
      if (data) applyDetail(data);
    } catch (e) {
      setFailure(readBdcError(e, "โหลด CN ไม่สำเร็จ"));
    } finally {
      setLoading(false);
    }
  }, [isNew, uuid, applyDetail]);

  useEffect(() => {
    load();
  }, [load]);

  // ทะเบียน INV › สร้าง CN: the INV fixes the DC and opens in the picker
  useEffect(() => {
    if (!fromInvoiceNo) return;
    let active = true;
    getBdcInvoice(fromInvoiceNo)
      .then(({ data: { data } }) => {
        if (!active || !data) return;
        setHeader((current) => ({ ...current, dc_code: data.dc_code }));
        setInitialInvoice({
          invoice_no: data.invoice_no,
          po_number: data.po_number,
          is_test: data.is_test,
          invoice_date: data.invoice_date,
        });
      })
      .catch(() => {
        // the interceptor reports it; the form still opens empty
      });
    return () => {
      active = false;
    };
  }, [fromInvoiceNo]);

  const body = useMemo(() => saveBody(header, lines), [header, lines]);
  const dirty = isNew || JSON.stringify(body) !== savedBody;
  const problem = draftProblem(header, lines);
  const over = overCreditedLines(lines);
  const totals = editable
    ? cnTotals(lines)
    : { subtotal: detail?.subtotal ?? 0, vat_amount: detail?.vat_amount ?? 0, grand_total: detail?.grand_total ?? 0 };
  const dc = dcs.find((d) => d.dc_code === header.dc_code);
  const noteBranch = branches.find((b) => b.company_id === (detail?.company_id ?? dc?.company_id));
  const missingPrefix = editable && noteBranch && !noteBranch.cn_prefix && noteBranch.cn_prefix !== undefined;

  const setField = <K extends keyof CnDraftHeader>(key: K, value: CnDraftHeader[K]) =>
    setHeader((current) => ({ ...current, [key]: value }));
  const setLine = (key: string, patch: Partial<CnDraftLine>) =>
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  const removeLine = (key: string) =>
    setLines((current) => {
      const next = current.filter((line) => line.key !== key);
      if (next.length === 0) setLineIsTest(null);
      return next;
    });

  const addLines = (added: CnDraftLine[], isTest: boolean) => {
    if (lines.length > 0 && lineIsTest !== null && lineIsTest !== isTest) {
      setFailure({
        status: null,
        code: null,
        message: "INV จริงกับ INV ทดสอบอยู่ใน CN เดียวกันไม่ได้",
        invoiceNo: null,
        poNumbers: [],
      });
      return;
    }
    setLineIsTest(isTest);
    setLines((current) => {
      const next = [...current];
      for (const line of added) {
        const at = next.findIndex((l) => l.key === line.key);
        if (at >= 0) next[at] = line;
        else next.push(line);
      }
      return next;
    });
  };

  /** Save the draft; resolves to the stored CN (or null when refused). */
  const save = async (): Promise<BdcCreditNoteDetail | null> => {
    if (problem) return null;
    setFailure(null);
    try {
      const {
        data: { data },
      } = isNew ? await createBdcCreditNote(body) : await updateBdcCreditNote(uuid, body);
      if (!data) return null;
      if (isNew) {
        navigate(bdcCreditNoteLink(data.uuid), { replace: true });
      } else {
        applyDetail(data);
      }
      return data;
    } catch (e) {
      setFailure(readBdcError(e, "บันทึกร่าง CN ไม่สำเร็จ"));
      return null;
    }
  };

  const runSave = async () => {
    setBusy("save");
    try {
      await save();
    } finally {
      setBusy(null);
    }
  };

  const runIssue = async () => {
    setBusy("issue");
    setFailure(null);
    try {
      let target = uuid;
      if (dirty) {
        const saved = await save();
        if (!saved) return;
        target = saved.uuid;
      }
      const {
        data: { data },
      } = await issueBdcCreditNote(target);
      setConfirm(null);
      if (target !== uuid) navigate(bdcCreditNoteLink(target), { replace: true });
      else if (data) applyDetail(data);
    } catch (e) {
      setConfirm(null);
      setFailure(readBdcError(e, "ออก CN ไม่สำเร็จ"));
    } finally {
      setBusy(null);
    }
  };

  const runDelete = async () => {
    setBusy("delete");
    try {
      await deleteBdcCreditNote(uuid);
      navigate(BDC_CREDIT_NOTES_PATH);
    } catch {
      // reported by the interceptor
    } finally {
      setBusy(null);
      setConfirm(null);
    }
  };

  const openPdf = async () => {
    if (!detail) return;
    setBusy("pdf");
    try {
      openPdfResponse(await getBdcCreditNotePdf(detail.uuid), `${detail.note_no ?? detail.uuid}.pdf`);
    } catch {
      // reported by the interceptor
    } finally {
      setBusy(null);
    }
  };

  const cancelNote = async (reason: string) => {
    if (!detail) return false;
    try {
      const {
        data: { data },
      } = await cancelBdcCreditNote(detail.uuid, reason);
      if (data) applyDetail(data);
      else load();
      return true;
    } catch {
      return false;
    }
  };

  const pickDate = (value: Dayjs | null) => {
    if (value && value.isValid()) setField("note_date", value.format("YYYY-MM-DD"));
  };

  const title = isNew ? "สร้าง CN" : detail?.note_no ? `CN ${detail.note_no}` : "CN (ร่าง)";

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap" mb={2}>
        <Box>
          <Stack direction="row" alignItems="center" gap={1} sx={TABLE_SX}>
            <Typography variant="h6" fontWeight={700}>
              BDC · {title}
            </Typography>
            {detail && <CreditNoteStatusChip note={detail} />}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {editable
              ? "ร่างแก้ / ลบได้ · ออก CN แล้วได้เลขตามสาขา (ทดสอบ TC…) และแก้ไขไม่ได้"
              : "ออก CN แล้ว — แก้ไขไม่ได้ · ยกเลิกได้จนกว่าจะสร้างไฟล์ RETSEV หรืออยู่ในใบวางบิล"}
          </Typography>
        </Box>
        <Button variant="outlined" component={RouterLink} to={BDC_CREDIT_NOTES_PATH} startIcon={<ArrowBackIcon />}>
          รายการ CN
        </Button>
      </Stack>

      <Stack gap={2} sx={TABLE_SX}>
        {loading && <LinearProgress />}
        {failure && (
          <Alert severity="error" onClose={() => setFailure(null)} data-testid="bdc-cn-error">
            {failure.message}
          </Alert>
        )}
        {missingPrefix && (
          <Alert severity="warning">
            สาขา {noteBranch?.branch_code} ยังไม่ได้ตั้ง prefix CN — บันทึกร่างได้ แต่ออก CN ไม่ได้ ·{" "}
            <RouterLink to={bdcSettingsLink("branch")}>ตั้งค่า BDC › สาขา</RouterLink> (MASTER)
          </Alert>
        )}
        {detail?.status === "cancelled" && (
          <Alert severity="error">
            ยกเลิกแล้ว: {detail.cancel_reason} · {detail.cancelled_by_name ?? ""} {thaiDateTime(detail.cancelled_at)}
          </Alert>
        )}

        <Card sx={{ ...CARD_SX, p: 2 }}>
          {editable ? (
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
              <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="flex-start">
                <TextField
                  select
                  size="small"
                  label="DC"
                  value={header.dc_code}
                  onChange={(e) => setField("dc_code", e.target.value)}
                  disabled={lines.length > 0}
                  helperText={lines.length > 0 ? "ลบรายการทั้งหมดก่อนเปลี่ยน DC" : " "}
                  sx={{ minWidth: 220 }}
                  slotProps={{ htmlInput: { "aria-label": "DC" } }}
                >
                  {dcs
                    .filter((d) => d.company_id)
                    .map((d) => (
                      <MenuItem key={d.dc_code} value={d.dc_code}>
                        {d.dc_code} {d.dc_name}
                      </MenuItem>
                    ))}
                </TextField>
                <TextField
                  size="small"
                  label="เลขเอกสารคืน 7-11"
                  value={header.return_doc_no}
                  onChange={(e) => setField("return_doc_no", e.target.value.replace(/\s/g, ""))}
                  helperText="ตัวเลขตามใบคืนของ 7-11"
                  sx={{ width: 190 }}
                  slotProps={{ htmlInput: { maxLength: BDC_RETURN_DOC_NO_LENGTH, inputMode: "numeric" } }}
                />
                <DatePicker
                  label="วันที่ CN"
                  value={dayjs(header.note_date)}
                  format="DD/MM/YYYY"
                  onChange={pickDate}
                  slotProps={{ textField: { size: "small", sx: { width: 170 }, helperText: " " } }}
                />
                <TextField
                  size="small"
                  label="เหตุผล"
                  value={header.reason}
                  onChange={(e) => setField("reason", e.target.value)}
                  helperText=" "
                  sx={{ flex: "1 1 260px" }}
                  slotProps={{ htmlInput: { maxLength: 500 } }}
                />
              </Stack>
            </LocalizationProvider>
          ) : (
            detail && (
              <Stack direction="row" gap={4} flexWrap="wrap">
                {[
                  ["เลข CN", <span className="mono">{detail.note_no ?? "-"}</span>],
                  ["วันที่", thaiDate(detail.note_date)],
                  [
                    "สาขา",
                    <BranchChip branches={branches} companyId={detail.company_id} branchCode={detail.branch_code} />,
                  ],
                  [
                    "DC",
                    <>
                      <span className="dc">{detail.dc_code}</span> {detail.dc_name ?? ""}
                    </>,
                  ],
                  ["เลขเอกสารคืน 7-11", <span className="mono">{detail.return_doc_no}</span>],
                  ["เหตุผล", detail.reason],
                  ["ออกโดย", `${detail.issued_by_name ?? ""} ${thaiDateTime(detail.issued_at)}`],
                  ...(detail.queued_at ? [["ส่ง 7-11", thaiDateTime(detail.queued_at)]] : []),
                  ...(detail.billing_no ? [["ใบวางบิล", <span className="mono">{detail.billing_no}</span>]] : []),
                ].map(([label, value]) => (
                  <Box key={label as string}>
                    <Typography variant="caption" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography variant="body2" component="div">
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )
          )}
        </Card>

        <Card sx={{ ...CARD_SX, p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Typography fontWeight={600}>รายการที่ลดหนี้</Typography>
          <Box sx={{ overflowX: "auto" }}>
            <table className="bdc-t" data-testid="bdc-cn-lines">
              <thead>
                <tr>
                  <th>#</th>
                  <th>INV / PO</th>
                  <th>อ้างอิง</th>
                  <th>สินค้า</th>
                  <th className="n">ราคา/ชิ้น</th>
                  <th className="n">จำนวน</th>
                  {editable && <th className="n">ลดได้</th>}
                  <th className="n">มูลค่า</th>
                  <th className="n">VAT</th>
                  {editable && <th />}
                </tr>
              </thead>
              <tbody>
                {editable
                  ? lines.map((line, i) => {
                      const qty = parseQty(line.qty);
                      const total = qty === null ? 0 : cnLineTotal(qty, line.unit_price);
                      const isOver = qty !== null && line.available !== null && qty > line.available;
                      return (
                        <tr key={line.key}>
                          <td>{i + 1}</td>
                          <td className="mono">
                            {line.invoice_no}
                            <div className="sub">
                              <RouterLink to={bdcShipmentLink(line.po_number, "inv")}>{line.po_number}</RouterLink>
                            </div>
                          </td>
                          <td>
                            <TextField
                              size="small"
                              value={line.ref_no}
                              onChange={(e) => setLine(line.key, { ref_no: e.target.value })}
                              sx={{ width: 140 }}
                              slotProps={{ htmlInput: { maxLength: BDC_CREDIT_NOTE_REF_NO_MAX, "aria-label": `อ้างอิง ${line.key}` } }}
                            />
                          </td>
                          <td>
                            {line.name} {line.size ?? ""}
                            <div className="sub mono">
                              {line.item_code} · {line.barcode}
                            </div>
                          </td>
                          <td className="n">{baht(line.unit_price)}</td>
                          <td className="n">
                            <TextField
                              size="small"
                              value={line.qty}
                              onChange={(e) => setLine(line.key, { qty: e.target.value })}
                              error={qty === null || isOver}
                              sx={{ width: 90 }}
                              slotProps={{ htmlInput: { inputMode: "numeric", "aria-label": `จำนวน ${line.key}` } }}
                            />
                          </td>
                          <td className="n" style={isOver ? { color: "#C62828", fontWeight: 600 } : undefined}>
                            {line.available === null ? "…" : pcs(line.available)}
                          </td>
                          <td className="n">{baht(total)}</td>
                          <td className="n">{baht(cnLineVat(total))}</td>
                          <td>
                            <IconButton size="small" aria-label={`ลบ ${line.key}`} onClick={() => removeLine(line.key)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </td>
                        </tr>
                      );
                    })
                  : (detail?.items ?? []).map((item) => (
                      <tr key={`${item.line_no}-${item.barcode}`}>
                        <td>{item.line_no}</td>
                        <td className="mono">
                          {item.invoice_no}
                          <div className="sub">{item.po_number}</div>
                        </td>
                        <td className="mono">{item.ref_no}</td>
                        <td>
                          {item.name} {item.size ?? ""}
                          <div className="sub mono">
                            {item.item_code} · {item.barcode}
                          </div>
                        </td>
                        <td className="n">{baht(item.unit_price)}</td>
                        <td className="n">{pcs(item.qty)}</td>
                        <td className="n">{baht(item.line_total)}</td>
                        <td className="n">{baht(item.vat_amount)}</td>
                      </tr>
                    ))}
                {(editable ? lines.length : detail?.items.length ?? 0) === 0 && (
                  <tr>
                    <td colSpan={10} className="sub" style={{ textAlign: "center", padding: 16 }}>
                      ยังไม่มีรายการ{editable ? " — เลือก INV ด้านล่าง" : ""}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Box>
          {editable && over.length > 0 && (
            <Alert severity="warning" data-testid="bdc-cn-over">
              {over.map((l) => `${l.item_code} (${l.invoice_no})`).join(", ")} เกินยอดที่ลดได้ — บันทึกร่างได้
              แต่ออก CN ไม่ได้จนกว่าจะแก้
            </Alert>
          )}
          <TotalsBox subtotal={totals.subtotal} vat={totals.vat_amount} total={totals.grand_total} />
        </Card>

        {editable && (
          <Card sx={{ ...CARD_SX, p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography fontWeight={600}>เพิ่มสินค้าจาก INV ของ {header.dc_code || "DC"}</Typography>
            <InvoicePicker
              dcCode={header.dc_code}
              noteDate={header.note_date}
              lines={lines}
              initialInvoice={initialInvoice}
              onAdd={addLines}
            />
          </Card>
        )}

        <Stack direction="row" gap={1} justifyContent="flex-end" flexWrap="wrap" alignItems="center">
          {editable && (
            <>
              <Typography className="sub" sx={{ mr: "auto" }}>
                {problem ?? (dirty ? "มีการแก้ไขที่ยังไม่บันทึก" : "บันทึกแล้ว")}
              </Typography>
              {!isNew && (
                <Button color="error" variant="outlined" onClick={() => setConfirm("delete")} disabled={busy !== null}>
                  ลบร่าง
                </Button>
              )}
              <Button variant="outlined" disabled={Boolean(problem) || !dirty} loading={busy === "save"} onClick={runSave}>
                บันทึกร่าง
              </Button>
              <Button
                variant="contained"
                color="warning"
                disabled={Boolean(problem) || over.length > 0 || busy !== null}
                onClick={() => setConfirm("issue")}
              >
                ออก CN
              </Button>
            </>
          )}
          {!editable && detail && detail.status !== "draft" && (
            <>
              <Button variant="outlined" startIcon={<PictureAsPdfIcon />} loading={busy === "pdf"} onClick={openPdf}>
                PDF
              </Button>
              {canWrite && canCancelNote(detail) && (
                <Button color="error" variant="outlined" onClick={() => setCancelOpen(true)}>
                  ยกเลิก CN
                </Button>
              )}
            </>
          )}
        </Stack>
      </Stack>

      <Dialog open={confirm !== null} onClose={busy ? undefined : () => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirm === "delete" ? "ลบร่าง CN" : "ออก CN"}</DialogTitle>
        <DialogContent>
          {confirm === "issue" && (
            <DialogContentText component="div">
              ออก CN ของ {header.dc_code} เลขคืน {header.return_doc_no} รวม <b>฿{baht(totals.grand_total)}</b> (
              {pcs(lines.length)} รายการ)
              <br />
              ระบบจะให้เลข CN ตามสาขา{lineIsTest ? " (ชุดทดสอบ TC…)" : ""} แล้วแก้ไขไม่ได้อีก
              {dirty && " · บันทึกการแก้ไขก่อนออก"}
            </DialogContentText>
          )}
          {confirm === "delete" && <DialogContentText>ร่างนี้จะถูกลบถาวร</DialogContentText>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy !== null}>
            ยกเลิก
          </Button>
          {confirm === "issue" ? (
            <Button variant="contained" color="warning" loading={busy === "issue"} onClick={runIssue}>
              ยืนยันออก CN
            </Button>
          ) : (
            <Button variant="contained" color="error" loading={busy === "delete"} onClick={runDelete}>
              ลบร่าง
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ReasonDialog
        open={cancelOpen}
        title={`ยกเลิก CN ${detail?.note_no ?? ""}`}
        warning={
          <>
            เลข {detail?.note_no} จะถูกบันทึกเป็น "ยกเลิก" และใช้ซ้ำไม่ได้ · ยอดที่ลดของ INV จะคืนกลับ (ลดหนี้ใหม่ได้)
          </>
        }
        confirmLabel="ยืนยันยกเลิก CN"
        onClose={() => setCancelOpen(false)}
        onConfirm={cancelNote}
      />
    </Box>
  );
}
